/**
 * Form error handling utilities
 *
 * This module provides utilities for handling form validation errors,
 * particularly for mapping API validation errors to form fields.
 */

import { APIError } from './api-client';

/**
 * Extract field-level validation errors from an APIError
 *
 * This is particularly useful for 422 Unprocessable Entity responses
 * where the API returns field-specific validation errors.
 *
 * @param error - The error from the API call
 * @returns Record of field names to error messages
 *
 * @example
 * ```typescript
 * try {
 *   await createFund(formData);
 * } catch (error) {
 *   const fieldErrors = extractValidationErrors(error);
 *   setErrors(fieldErrors);
 * }
 * ```
 */
export function extractValidationErrors(
  error: unknown
): Record<string, string> {
  if (error instanceof APIError && error.validationErrors) {
    return error.validationErrors;
  }
  return {};
}

/**
 * Merge client-side validation errors with server-side validation errors
 *
 * Server-side errors take precedence over client-side errors for the same field.
 *
 * @param clientErrors - Errors from client-side validation
 * @param serverErrors - Errors from server-side validation (API response)
 * @returns Merged error record
 */
export function mergeValidationErrors(
  clientErrors: Record<string, string>,
  serverErrors: Record<string, string>
): Record<string, string> {
  return {
    ...clientErrors,
    ...serverErrors, // Server errors override client errors
  };
}

/**
 * Check if there are any validation errors
 *
 * @param errors - Error record
 * @returns True if there are any errors
 */
export function hasValidationErrors(errors: Record<string, string>): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Clear specific field errors
 *
 * @param errors - Current error record
 * @param fieldNames - Field names to clear errors for
 * @returns New error record without the specified fields
 */
export function clearFieldErrors(
  errors: Record<string, string>,
  ...fieldNames: string[]
): Record<string, string> {
  const newErrors = { ...errors };
  for (const fieldName of fieldNames) {
    delete newErrors[fieldName];
  }
  return newErrors;
}

/**
 * Scroll the first errored form field into view and focus it.
 */
export function scrollToFirstError(errors: Record<string, string>): void {
  const firstField = Object.keys(errors)[0];
  if (!firstField) return;

  const element =
    document.getElementById(firstField) ||
    document.querySelector<HTMLElement>(`[name="${firstField}"]`);
  if (!(element instanceof HTMLElement)) return;

  element.focus({ preventScroll: true });
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

interface MutationErrorHandlerOptions {
  /** Fallback message when the error carries no field-level validation errors */
  fallbackMessage?: string;
  /** Rename backend field names to the form's local field names (e.g. cost_date -> date) */
  fieldMap?: Record<string, string>;
  /** Called in addition to setErrors when field-level errors were found (e.g. a toast) */
  onValidationError?: (fieldErrors: Record<string, string>) => void;
  /**
   * Called instead of setting a `general` key when the error has no field mapping.
   * Use this when the form has no `general` slot to render (e.g. relies on the
   * mutation's own toast instead) or routes fallback messages to separate state.
   */
  onFallbackError?: (message: string) => void;
}

/**
 * Build a mutation `onError` handler that routes API validation errors to
 * form field state, and falls back to a general message otherwise.
 *
 * Consolidates the extract -> merge -> scroll -> fallback sequence that was
 * being hand-rolled per form. Forms whose error state is a closed type
 * (rather than this codebase's usual `Record<string, string>`) pass their
 * setter cast to that shape at the call site.
 */
export function buildMutationErrorHandler(
  setErrors: (
    updater: (prev: Record<string, string>) => Record<string, string>
  ) => void,
  options: MutationErrorHandlerOptions = {}
): (error: unknown) => void {
  const { fallbackMessage, fieldMap, onValidationError, onFallbackError } =
    options;

  return (error: unknown) => {
    const rawErrors = extractValidationErrors(error);
    const fieldErrors = fieldMap
      ? Object.fromEntries(
          Object.entries(rawErrors).map(([field, message]) => [
            fieldMap[field] ?? field,
            message,
          ])
        )
      : rawErrors;

    if (hasValidationErrors(fieldErrors)) {
      setErrors((prev) => mergeValidationErrors(prev, fieldErrors));
      scrollToFirstError(fieldErrors);
      onValidationError?.(fieldErrors);
      return;
    }

    const message =
      error instanceof Error
        ? error.message
        : fallbackMessage || 'Something went wrong';

    if (onFallbackError) {
      onFallbackError(message);
    } else {
      setErrors(() => ({ general: message }));
    }
  };
}
